import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  is_superuser: boolean;
  is_approved: boolean;
  approval_status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  approved_at: string | null;
}

interface Stats {
  total_users: number;
  pending_users: number;
  approved_users: number;
  rejected_users: number;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Fetch users and stats
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine status filter based on tab
      const statusMap: Record<number, string | undefined> = {
        0: undefined, // All
        1: 'pending',
        2: 'approved',
        3: 'rejected',
      };
      const statusFilter = statusMap[tabValue];
      
      // Fetch users
      const params: any = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const usersResponse = await api.get('/api/admin/all-users', { params });
      setUsers(usersResponse.data);
      
      // Fetch stats
      const statsResponse = await api.get('/api/admin/stats');
      setStats(statsResponse.data);
      
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  // Handle search
  const handleSearch = () => {
    fetchData();
  };

  // Handle approve user
  const handleApprove = async (user: User) => {
    try {
      setActionLoading(user.id);
      setError(null);
      
      await api.post(`/api/admin/approve-user/${user.id}`);
      
      setSuccess(`✅ ${user.email} has been approved`);
      fetchData();
      
    } catch (err: any) {
      console.error('Error approving user:', err);
      setError(err.response?.data?.detail || 'Failed to approve user');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject user
  const handleReject = async () => {
    if (!selectedUser) return;
    
    try {
      setActionLoading(selectedUser.id);
      setError(null);
      
      await api.post(`/api/admin/reject-user/${selectedUser.id}`, {
        user_id: selectedUser.id,
        reason: rejectionReason || 'Registration declined by admin'
      });
      
      setSuccess(`❌ ${selectedUser.email} has been rejected`);
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedUser(null);
      fetchData();
      
    } catch (err: any) {
      console.error('Error rejecting user:', err);
      setError(err.response?.data?.detail || 'Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  // Open reject dialog
  const openRejectDialog = (user: User) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
  };

  // Close reject dialog
  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
    setSelectedUser(null);
  };

  // Open delete dialog
  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletionReason('');
    setSelectedUser(null);
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    console.log('🗑️ Deleting user:', selectedUser.email, 'ID:', selectedUser.id);
    console.log('🗑️ Deletion reason:', deletionReason);
    
    try {
      setActionLoading(selectedUser.id);
      setError(null);
      
      const requestBody = deletionReason ? { reason: deletionReason } : {};
      console.log('🗑️ Request body:', requestBody);
      console.log('🗑️ Making DELETE request to:', `/api/admin/users/${selectedUser.id}`);
      
      const response = await api.delete(`/api/admin/users/${selectedUser.id}`, {
        data: requestBody
      });
      
      console.log('✅ Delete response:', response.data);
      
      setSuccess(`🗑️ ${selectedUser.email} has been deleted`);
      setDeleteDialogOpen(false);
      setDeletionReason('');
      setSelectedUser(null);
      fetchData();
      
    } catch (err: any) {
      console.error('❌ Error deleting user:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  // Get status chip
  const getStatusChip = (status: string) => {
    const config = {
      pending: { label: 'Pending', color: 'warning' as const, icon: <PendingIcon /> },
      approved: { label: 'Approved', color: 'success' as const, icon: <CheckIcon /> },
      rejected: { label: 'Rejected', color: 'error' as const, icon: <BlockIcon /> },
    };
    
    const { label, color, icon } = config[status as keyof typeof config];
    return <Chip icon={icon} label={label} color={color} size="small" />;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/dashboard')}
          sx={{ 
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'action.hover',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user registrations and approvals
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {stats.total_users}
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Pending
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {stats.pending_users}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Approved
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {stats.approved_users}
                    </Typography>
                  </Box>
                  <CheckIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Rejected
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                      {stats.rejected_users}
                    </Typography>
                  </Box>
                  <BlockIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ width: '100%' }}>
        {/* Tabs */}
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`All (${stats?.total_users || 0})`} />
          <Tab label={`Pending (${stats?.pending_users || 0})`} />
          <Tab label={`Approved (${stats?.approved_users || 0})`} />
          <Tab label={`Rejected (${stats?.rejected_users || 0})`} />
        </Tabs>

        {/* Search Bar */}
        <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by email, username, or full name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleSearch} startIcon={<SearchIcon />}>
            Search
          </Button>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Table */}
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <Typography color="text.secondary">No users found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      {getStatusChip(user.approval_status)}
                      {user.rejection_reason && (
                        <Tooltip title={user.rejection_reason}>
                          <Chip label="View reason" size="small" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(user.created_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell align="right">
                      {user.approval_status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={actionLoading === user.id ? <CircularProgress size={16} /> : <CheckIcon />}
                            onClick={() => handleApprove(user)}
                            disabled={actionLoading === user.id}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openRejectDialog(user)}
                            disabled={actionLoading === user.id}
                          >
                            Reject
                          </Button>
                          <Tooltip title="Delete Account">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(user)}
                              disabled={actionLoading === user.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {user.approval_status === 'approved' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Approved {user.approved_at && `on ${format(new Date(user.approved_at), 'MMM d, yyyy')}`}
                          </Typography>
                          <Tooltip title="Delete Account">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(user)}
                              disabled={actionLoading === user.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {user.approval_status === 'rejected' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Typography variant="body2" color="error">
                            Rejected
                          </Typography>
                          <Tooltip title="Delete Account">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(user)}
                              disabled={actionLoading === user.id}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject User Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{selectedUser?.email}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (Optional)"
            placeholder="e.g., Invalid credentials, spam account, etc."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!!actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            Reject User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User Account</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently deactivate the user account and cannot be undone.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to delete the account for <strong>{selectedUser?.email}</strong>?
          </Typography>
          {selectedUser && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">User Details:</Typography>
              <Typography variant="body2">Email: {selectedUser.email}</Typography>
              <Typography variant="body2">Username: {selectedUser.username}</Typography>
              <Typography variant="body2">Full Name: {selectedUser.full_name || 'N/A'}</Typography>
              <Typography variant="body2">Status: {selectedUser.approval_status}</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Deletion Reason (Optional)"
            placeholder="e.g., Policy violation, user request, spam account, etc."
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={!!actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
